import { t } from '../i18n/i18n';
import { useMemo, useState } from 'react';
import { flattenPath } from '../engine/geometry/bezier';
import {
  PATCH_LIMITS,
  PATCH_SHAPE_NAMES,
  type PatchOptions,
  type PatchShape,
  applyPatch,
  clampPatchOptions,
  defaultPatchOptions,
  designOnlyBBox,
  hasPatch,
  patchOutline,
  removePatch,
} from '../engine/patch';
import { store, useApp } from '../state/store';
import { objectPath } from './canvas/shapes';
import { IconClose } from './icons';
import { NumberField } from './NumberField';

const SHAPES: PatchShape[] = ['circle', 'oval', 'rect', 'rounded', 'shield', 'contour'];

export function PatchDialog() {
  const design = useApp((s) => s.history.present);
  const [o, setO] = useState<PatchOptions>(() => defaultPatchOptions(design));
  const close = () => store.set({ panel: null });
  const set = <K extends keyof PatchOptions>(k: K, v: PatchOptions[K]) => setO((x) => ({ ...x, [k]: v }));
  // hidden objects are not sewn, so they are not part of the patch preview or sizing
  const designObjects = design.objects.filter((x) => !x.patch && x.visible);
  const empty = designObjects.length === 0;

  const preview = useMemo(() => {
    const outline = flattenPath(patchOutline(design, o), true, 0.1);
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of outline) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
    const db = designOnlyBBox(design);
    if (db) {
      minX = Math.min(minX, db.minX);
      minY = Math.min(minY, db.minY);
      maxX = Math.max(maxX, db.maxX);
      maxY = Math.max(maxY, db.maxY);
    }
    return { d: 'M' + outline.map((p) => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join('L') + 'Z', box: { minX, minY, maxX, maxY }, w: maxX - minX, h: maxY - minY };
  }, [design, o]);

  const centerOnDesign = () => {
    const b = designOnlyBBox(design);
    if (b) set('center', { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 });
  };

  const apply = () => {
    store.commitDesign(applyPatch(design, clampPatchOptions(o)));
    store.set((s) => ({ panel: null, fitNonce: s.fitNonce + 1 }));
    store.toast('success', o.style === 'applique' ? t('Patch added. The machine stops after the placement line (lay the fabric) and after the tack-down (trim away the excess).') : t('Patch added: background, design and satin border.'));
  };

  const pad = Math.max(preview.w, preview.h) * 0.06 + 1;
  const colorOf = new Map(design.palette.map((c) => [c.id, c.hex]));

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="patch-title" onKeyDown={(e) => e.key === 'Escape' && close()}>
      <div className="modal">
        <div className="modal-head">
          <h2 id="patch-title">{t('Make patch')}</h2>
          <button className="btn icon ghost" aria-label={t('Close dialog')} onClick={close}>
            <IconClose />
          </button>
        </div>
        <div className="modal-body">
          <div className="import-grid">
            <div className="preview-box" style={{ background: '#efeae0' }}>
              <svg
                viewBox={`${preview.box.minX - pad} ${preview.box.minY - pad} ${preview.w + 2 * pad} ${preview.h + 2 * pad}`}
                style={{ width: '100%', height: '100%' }}
                aria-label={t('Patch preview')}
                role="img"
              >
                {o.style === 'embroidered' && <path d={preview.d} fill={o.backgroundColor} />}
                {o.style === 'applique' && <path d={preview.d} fill="#d9d2c3" stroke={o.borderColor} strokeDasharray="2 1.5" strokeWidth={0.4} />}
                {designObjects.map((x) => {
                  const p = objectPath(x);
                  const c = colorOf.get(x.colorId) ?? '#888';
                  return <path key={x.id} d={p.outline ?? p.d} fill={p.closed || p.outline ? c : 'none'} stroke={c} strokeWidth={p.closed ? 0 : 1} fillRule="evenodd" />;
                })}
                {/* stroke is clipped to the inside, so 2 × border width shows the real border width */}
                <path d={preview.d} fill="none" stroke={o.borderColor} strokeWidth={o.borderWidth * 2} strokeLinejoin="round" clipPath="url(#patch-inside)" />
                <defs>
                  <clipPath id="patch-inside">
                    <path d={preview.d} />
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div>
              {empty && <p className="issue warning">{t('The design is empty. The patch is created anyway, but import or draw the motif first to fit the size.')}</p>}
              <div className="section" style={{ padding: '0 0 10px' }}>
                <h3>{t('Shape')}</h3>
                <div className="row wrap" role="radiogroup" aria-label={t('Patch shape')}>
                  {SHAPES.map((s) => (
                    <button key={s} className={'btn small' + (o.shape === s ? ' active' : '')} role="radio" aria-checked={o.shape === s} onClick={() => set('shape', s)}>
                      {t(PATCH_SHAPE_NAMES[s])}
                    </button>
                  ))}
                </div>
              </div>
              <div className="section" style={{ padding: '10px 0' }}>
                <h3>{t('Method')}</h3>
                <label className="check">
                  <input type="radio" name="patch-style" checked={o.style === 'embroidered'} onChange={() => set('style', 'embroidered')} /> {t('Fully embroidered (filled background)')}
                </label>
                <label className="check">
                  <input type="radio" name="patch-style" checked={o.style === 'applique'} onChange={() => set('style', 'applique')} /> {t('Appliqué on fabric (twill)')}
                </label>
                <p className="hint">
                  {o.style === 'embroidered'
                    ? t('The background is filled with stitches. Sew on stabilizer and cut out along the edge.')
                    : t('Placement line → stop (lay the fabric) → tack-down → stop (trim away the fabric outside) → design → satin border.')}
                </p>
              </div>
              <div className="section" style={{ padding: '10px 0' }}>
                <h3>{t('Dimensions')}</h3>
                <div className="grid2">
                  {o.shape === 'contour' ? (
                    <NumberField label={t('Distance to the design')} limit={PATCH_LIMITS.contourMargin} value={o.contourMargin} onChange={(v) => set('contourMargin', v)} />
                  ) : o.shape === 'circle' ? (
                    <NumberField label={t('Diameter')} limit={PATCH_LIMITS.size} decimals={1} value={Math.min(o.widthMm, o.heightMm)} onChange={(v) => setO((x) => ({ ...x, widthMm: v, heightMm: v }))} />
                  ) : (
                    <>
                      <NumberField label={t('Width')} limit={PATCH_LIMITS.size} decimals={1} value={o.widthMm} onChange={(v) => set('widthMm', v)} />
                      <NumberField label={t('Height')} limit={PATCH_LIMITS.size} decimals={1} value={o.heightMm} onChange={(v) => set('heightMm', v)} />
                    </>
                  )}
                  <NumberField label={t('Border width (satin)')} limit={PATCH_LIMITS.borderWidth} value={o.borderWidth} onChange={(v) => set('borderWidth', v)} />
                  {o.shape === 'rounded' && <NumberField label={t('Corner radius')} limit={PATCH_LIMITS.cornerRadius} value={o.cornerRadius} onChange={(v) => set('cornerRadius', v)} />}
                </div>
                {o.shape !== 'contour' && (
                  <button className="btn small ghost" style={{ marginTop: 6 }} onClick={centerOnDesign} disabled={empty}>
                    {t('Centre on the design')}
                  </button>
                )}
                <p className="hint">{t('The outer edge of the satin border is the edge of the patch. Actual size: {w} × {h} mm.', { w: preview.w.toFixed(1), h: preview.h.toFixed(1) })}</p>
              </div>
              <div className="section" style={{ padding: '10px 0', borderBottom: 0 }}>
                <h3>{t('Colours')}</h3>
                <div className="row wrap">
                  {o.style === 'embroidered' && (
                    <label className="row">
                      <input type="color" className="swatch-input" aria-label={t('Background colour')} value={o.backgroundColor} onChange={(e) => set('backgroundColor', e.target.value)} /> {t('Background')}
                    </label>
                  )}
                  <label className="row">
                    <input type="color" className="swatch-input" aria-label={t('Border colour')} value={o.borderColor} onChange={(e) => set('borderColor', e.target.value)} /> {t('Border')}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-foot">
          {hasPatch(design) && (
            <button
              className="btn danger"
              onClick={() => {
                store.commitDesign(removePatch(design));
                close();
              }}
            >
              {t('Remove patch')}
            </button>
          )}
          <span className="spacer" />
          <button className="btn" onClick={close}>
            {t('Cancel')}
          </button>
          <button className="btn primary" onClick={apply}>
            {hasPatch(design) ? t('Update patch') : t('Make patch')}
          </button>
        </div>
      </div>
    </div>
  );
}
